import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import PagesEndpoints from "../../utilities/constants/PagesEndpoints";

import FacilityConstants from "../../utilities/constants/FacilityConstants";
import { Services } from "../../utilities/models/ServiceModel";

// Custom SVG icons for social media (replacing deprecated lucide brand icons)
const FacebookIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
      clipRule="evenodd"
    />
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
      clipRule="evenodd"
    />
  </svg>
);

const pages = [
  { title: "About", pageUrl: PagesEndpoints.ABOUT_PAGE_URL },
  { title: "Services", pageUrl: PagesEndpoints.SERVICES_PAGE_URL },
  { title: "Events", pageUrl: PagesEndpoints.EVENT_PAGE_URL },
  { title: "Gallery", pageUrl: PagesEndpoints.GALLERY_PAGE_URL },
  { title: "Contact Us", pageUrl: PagesEndpoints.CONTACT_PAGE_URL },
  { title: "Loved by Locals", pageUrl: PagesEndpoints.REVIEWS_PAGE_URL },
];

const socialMedia = [
  {
    name: "Facebook",
    icon: FacebookIcon,
    pageUrl: "https://www.facebook.com/Le-Prestige-Hall-100183814879812",
  },
  //   {
  //     name: "Twitter",
  //     icon: Twitter,
  //     pageUrl: "https://www.twitter.com/le_prestige_hall",
  //   },
  {
    name: "Instagram",
    icon: InstagramIcon,
    pageUrl: "https://www.instagram.com/prestigehallcolumbus",
  },
  //   {
  //     name: "LinkedIn",
  //     icon: Linkedin,
  //     pageUrl: "https://www.linkedin.com/le_prestige_hall",
  //   },
];

const Footer = () => {
  const location = useLocation();
  const isAccountPage = location.pathname.includes(
    PagesEndpoints.ACCOUNT_PAGE_URL,
  );
  const thisYear = new Date().getFullYear();

  // Limit services to display in footer
  const footerServices = Services.slice(0, 5);

  // Scroll to top when navigating via footer links
  const scrollToTop = () => {
    globalThis.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isAccountPage) return null;

  return (
    <footer className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 transition-colors duration-300">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link
              to={PagesEndpoints.HOME_PAGE_URL}
              onClick={scrollToTop}
              className="flex items-center gap-2 group"
            >
              <img
                src="/assets/images/le_prestige_hall_logo.png"
                alt="Le Prestige Hall Logo"
                className="h-10 w-auto rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300"
              />
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                Le Prestige Hall
              </span>
            </Link>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              Elevating events to unforgettable experiences. Le Prestige Hall is
              your premier destination for weddings, corporate gatherings, and
              celebrations.
            </p>
            <div className="flex gap-4 pt-2">
              {socialMedia.map((item) => (
                <a
                  key={item.name}
                  href={item.pageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Column */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
              Navigation
            </h3>
            <ul className="space-y-3">
              {pages.map((page) => (
                <li key={page.title}>
                  <Link
                    to={page.pageUrl}
                    onClick={scrollToTop}
                    className="text-sm text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services / Extra Column */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
              Services
            </h3>
            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              {footerServices.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`${PagesEndpoints.SERVICES_PAGE_URL}#${service.id}`}
                    onClick={() => {
                      // Small delay to allow navigation before scrolling to element
                      setTimeout(() => {
                        const element = document.getElementById(service.id);
                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 100);
                    }}
                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={PagesEndpoints.SERVICES_PAGE_URL}
                  onClick={scrollToTop}
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors font-medium"
                >
                  View All →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
              Contact
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-400">
                <MapPin className="w-5 h-5 text-indigo-500 shrink-0" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${FacilityConstants.ADDRESS.Street}, ${FacilityConstants.ADDRESS.City}, ${FacilityConstants.ADDRESS.State} ${FacilityConstants.ADDRESS.ZipCode}`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {FacilityConstants.ADDRESS.Street}
                  <br />
                  {FacilityConstants.ADDRESS.City},{" "}
                  {FacilityConstants.ADDRESS.State}{" "}
                  {FacilityConstants.ADDRESS.ZipCode}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                <Phone className="w-5 h-5 text-indigo-500 shrink-0" />
                <a
                  href={`tel:${FacilityConstants.PHONE.format4}`}
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {FacilityConstants.PHONE.format1}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                <Mail className="w-5 h-5 text-indigo-500 shrink-0" />
                <a
                  href={`mailto:${FacilityConstants.EMAILS.info}`}
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {FacilityConstants.EMAILS.info}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 dark:text-slate-500">
            &copy; 2019-{thisYear}, Le Prestige Hall, Inc. All Rights Reserved.
          </p>
          <div className="flex gap-6 text-xs text-slate-500 dark:text-slate-500">
            <Link
              to={PagesEndpoints.PRIVACY_POLICY_PAGE_URL}
              onClick={scrollToTop}
              className="hover:text-slate-900 dark:hover:text-slate-300"
            >
              Privacy Policy
            </Link>
            <Link
              to={PagesEndpoints.TERMS_OF_SERVICE_PAGE_URL}
              onClick={scrollToTop}
              className="hover:text-slate-900 dark:hover:text-slate-300"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
